<template>
  <section class="d-grid gap-3">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h5 mb-1">Cierres de caja</h2>
        <p class="text-muted small mb-0">Declarado vs. esperado por cierre, con la diferencia ya persistida.</p>
      </div>
      <button class="btn btn-outline-dark btn-sm" :disabled="loading || !filas.length" @click="exportar">
        <i class="bi bi-download me-1"></i>Exportar CSV
      </button>
    </header>

    <div class="card shadow-sm">
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-auto">
            <label class="form-label small mb-1">Desde</label>
            <input v-model="filtros.fecha_desde" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-auto">
            <label class="form-label small mb-1">Hasta</label>
            <input v-model="filtros.fecha_hasta" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-auto">
            <label class="form-label small mb-1">Caja</label>
            <input v-model="filtros.caja_id" type="number" min="1" class="form-control form-control-sm" placeholder="Todas" style="width: 110px" />
          </div>
          <div class="col-auto">
            <button class="btn btn-dark btn-sm" :disabled="loading" @click="cargar">
              {{ loading ? 'Buscando...' : 'Buscar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger mb-0">{{ error }}</div>

    <div v-if="totales" class="row g-3">
      <div class="col-md-3">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Cierres</div>
          <div class="h5 mb-0">{{ totales.cantidad_cierres }}</div>
        </div></div>
      </div>
      <div class="col-md-3">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Diferencia total</div>
          <div class="h5 mb-0" :class="Number(totales.diferencia_total) !== 0 ? 'text-danger' : ''">
            {{ formatoMonto(totales.diferencia_total) }}
          </div>
        </div></div>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>Apertura</th><th>Cajero</th><th>Caja</th><th>Cierre</th>
              <th class="text-end">Efectivo</th><th class="text-end">Transferencia</th>
              <th class="text-end">Final</th><th class="text-end">Diferencia</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading"><td colspan="8" class="text-center py-4">Cargando...</td></tr>
            <tr v-else-if="!filas.length"><td colspan="8" class="text-center py-4 text-muted">Sin resultados</td></tr>
            <tr v-else v-for="f in filas" :key="f.apertura_id">
              <td>{{ f.apertura_id }}</td>
              <td>{{ f.cajero_nombre || '-' }}</td>
              <td>{{ f.caja_id }}</td>
              <td>{{ formatoFecha(f.fecha_cierre) }}</td>
              <td class="text-end">{{ formatoMonto(f.monto_efectivo) }}</td>
              <td class="text-end">{{ formatoMonto(f.monto_transferencia) }}</td>
              <td class="text-end">{{ formatoMonto(f.monto_final) }}</td>
              <td class="text-end" :class="Number(f.diferencia) !== 0 ? 'text-danger fw-bold' : ''">
                {{ formatoMonto(f.diferencia) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import reportsService from '@/services/reportsService'

const route = useRoute()
const hoy = new Date().toISOString().slice(0, 10)
const inicioMes = `${hoy.slice(0, 7)}-01`

// Deep-link desde una tarjeta del dashboard (DASH-4A-002).
const filtros = ref({
  fecha_desde: route.query.fecha_desde || inicioMes,
  fecha_hasta: route.query.fecha_hasta || hoy,
  caja_id: '',
})
const filas = ref([])
const totales = ref(null)
const loading = ref(false)
const error = ref('')

function formatoMonto(valor) {
  return new Intl.NumberFormat('es-PY', { maximumFractionDigits: 0 }).format(Number(valor) || 0)
}
function formatoFecha(valor) {
  return valor ? new Date(valor).toLocaleString('es-PY') : '-'
}

async function cargar() {
  loading.value = true
  error.value = ''
  try {
    const data = await reportsService.getCierresCaja(filtros.value)
    filas.value = data.filas
    totales.value = data.totales
  } catch (e) {
    error.value = e?.response?.data?.message || 'No se pudo cargar el reporte.'
  } finally {
    loading.value = false
  }
}

async function exportar() {
  try {
    await reportsService.descargarCsv('cierres-caja', filtros.value, 'cierres-caja')
  } catch (e) {
    error.value = 'No se pudo exportar el CSV.'
  }
}

onMounted(cargar)
</script>
